///
/// EarthBrowser
///
/// Created by Matt Giger on 12/13/2024
/// Copyright (c) 2024 Geodata Labs
///

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { BellIcon, CreditCardIcon, HardDriveIcon, LockIcon, ShieldIcon, SparklesIcon, UserIcon } from "lucide-react"
import { useState } from "react"
import { AccountTab } from "./AccountTab"
import { NotificationsSection } from "./NotificationsSection"
import { PrivacySection } from "./PrivacySection"
import { ProfileHeader } from "./ProfileHeader"
import { SectionHeader } from "./SectionHeader"
import { SecurityTab } from "./SecurityTab"
import { StorageSection } from "./StorageSection"
import { SubscriptionSection } from "./Subscriptions/SubscriptionSection"

export type ProfileTab = "account" | "security" | "subscription" | "storage" | "privacy" | "notifications"

const tabs: { id: ProfileTab, label: string, icon: typeof UserIcon }[] = [
	{ id: "account", label: "Account", icon: UserIcon },
	{ id: "security", label: "Security", icon: ShieldIcon },
	{ id: "subscription", label: "Billing", icon: CreditCardIcon },
	{ id: "storage", label: "Storage", icon: HardDriveIcon },
	{ id: "privacy", label: "Privacy", icon: LockIcon },
	{ id: "notifications", label: "Alerts", icon: BellIcon },
]

interface ProfileSectionProps {
	initialTab?: ProfileTab
}

export function ProfileSection({ initialTab = "account" }: ProfileSectionProps) {
	const userProfile = useAppSelector((state) => state.user.userProfile)
	const [tab, setTab] = useState<ProfileTab>(initialTab)

	if (!userProfile) {
		return (
			<div className="flex items-center justify-center p-12">
				<div className="text-center space-y-3">
					<SparklesIcon className="h-12 w-12 mx-auto text-muted-foreground animate-pulse" />
					<p className="text-muted-foreground">No profile data available</p>
				</div>
			</div>
		)
	}

	const tabContentClass = "min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 md:px-6 pb-6 pt-4 mt-0 focus-visible:ring-0"
	const activeLabel = tabs.find(t => t.id === tab)?.label ?? ""

	// Desktop: identity + vertical nav in a left column, content on the
	// right. Phones: identity on top, icon-only segmented row, content below.
	return (
		<Tabs
			value={tab}
			onValueChange={(value) => setTab(value as ProfileTab)}
			className="flex h-full min-h-0 flex-1 flex-col md:flex-row"
		>
			<div className="flex flex-col flex-shrink-0 md:w-56 md:border-r border-white/10 md:bg-white/[0.03] md:overflow-y-auto">
				<div className="px-4 pt-5 md:px-5 md:pt-6">
					<ProfileHeader />
				</div>

				<TabsList
					aria-label="Account sections"
					className={cn(
						"h-auto bg-transparent p-0 text-white/60",
						"mx-4 mt-4 mb-2 grid grid-cols-6 gap-1 rounded-xl bg-white/5 p-1",
						"md:mx-3 md:mt-4 md:mb-3 md:flex md:flex-col md:items-stretch md:gap-0.5 md:rounded-none md:bg-transparent md:p-0"
					)}
				>
					{tabs.map(({ id, label, icon: Icon }) => (
						<TabsTrigger
							key={id}
							value={id}
							title={label}
							className={cn(
								"rounded-lg px-1 py-1.5 gap-2.5 text-white/60 ring-offset-transparent",
								"hover:text-white hover:bg-white/5",
								"data-[state=active]:bg-white/10 data-[state=active]:text-white data-[state=active]:shadow-none",
								"dark:data-[state=active]:bg-white/10 dark:data-[state=active]:text-white",
								"focus-visible:ring-cyan-500/50 focus-visible:ring-offset-0 dark:focus-visible:ring-cyan-500/50",
								"md:justify-start md:px-3 md:py-1.5"
							)}
						>
							<Icon className="h-4 w-4 flex-shrink-0" />
							<span className="hidden md:inline text-sm">{label}</span>
						</TabsTrigger>
					))}
				</TabsList>
			</div>

			<div className="flex min-h-0 flex-1 flex-col">
				{/* Desktop section header: names the active section and keeps
				    the top-right corner clear for the dialog's close button
				    (pr-12). On phones the identity block already occupies the
				    top, so the content starts right under the segmented row. */}
				<div className="hidden md:flex items-center h-12 flex-shrink-0 px-6 pr-12 border-b border-white/10">
					<h2 className="text-sm font-semibold text-white truncate">{activeLabel}</h2>
				</div>

				<TabsContent value="account" className={tabContentClass}>
					<AccountTab />
				</TabsContent>

				<TabsContent value="security" className={tabContentClass}>
					<SecurityTab />
				</TabsContent>

				<TabsContent value="subscription" className={tabContentClass}>
					<div className="rounded-xl border border-border bg-muted/30 p-4">
						<SectionHeader
							icon={CreditCardIcon}
							title="Subscription"
							description="Manage your subscription and billing"
							color="amber"
						/>
						<SubscriptionSection />
					</div>
				</TabsContent>

				<TabsContent value="storage" className={tabContentClass}>
					<div className="rounded-xl border border-border bg-muted/30 p-4">
						<SectionHeader
							icon={HardDriveIcon}
							title="My Datasets"
							description="Imported files saved to your account, available on all your devices"
							color="teal"
						/>
						<StorageSection />
					</div>
				</TabsContent>

				<TabsContent value="privacy" className={tabContentClass}>
					<div className="rounded-xl border border-border bg-muted/30 p-4">
						<SectionHeader
							icon={ShieldIcon}
							title="Data & Privacy"
							description="Manage your data and privacy settings"
							color="purple"
						/>
						<PrivacySection />
					</div>
				</TabsContent>

				<TabsContent value="notifications" className={tabContentClass}>
					<div className="rounded-xl border border-border bg-muted/30 p-4">
						<SectionHeader
							icon={BellIcon}
							title="Notifications"
							description="Choose which emails you want to receive"
							color="teal"
						/>
						<NotificationsSection />
					</div>
				</TabsContent>
			</div>
		</Tabs>
	)
}
